/**
 * Logger Service - Centralized, Environment-Aware Logging
 *
 * Provides structured logging with automatic environment detection:
 * - Development: Console output with emoji indicators
 * - Production: Sentry integration with breadcrumbs
 *
 * All logging behavior is environment-driven (100% dynamic).
 */

import * as Sentry from '@sentry/react';

/**
 * Log metadata interface
 */
export interface LogMetadata {
  [key: string]: unknown;
}

/**
 * Centralized logging service with environment-aware behavior
 */
class LoggerService {
  /**
   * Determines if we're in development mode
   * Uses Vite's import.meta.env which is replaced at build time
   */
  private readonly isDevelopment: boolean = import.meta.env.DEV;

  /**
   * Informational messages
   * - Dev: Console output
   * - Prod: Sentry breadcrumb only
   *
   * @param message - Log message
   * @param meta - Optional metadata
   */
  public info(message: string, meta?: LogMetadata): void {
    if (this.isDevelopment) {
      console.info(`ℹ️ ${message}`, meta || '');
    }

    // Always add breadcrumb for Sentry context
    Sentry.addBreadcrumb({
      message,
      level: 'info',
      data: this.sanitizePayload(meta) as LogMetadata,
    });
  }

  /**
   * Warning messages
   * - Dev: Console warning
   * - Prod: Sentry breadcrumb
   *
   * @param message - Warning message
   * @param meta - Optional metadata
   */
  public warn(message: string, meta?: LogMetadata): void {
    if (this.isDevelopment) {
      console.warn(`⚠️ ${message}`, meta || '');
    }

    Sentry.addBreadcrumb({
      message,
      level: 'warning',
      data: this.sanitizePayload(meta) as LogMetadata,
    });
  }

  /**
   * Error logging with full Sentry capture
   * - Dev: Console error with full details
   * - Prod: Sentry exception capture
   *
   * @param message - Error message
   * @param error - Error object or unknown error
   * @param context - Optional context string for grouping
   */
  public error(message: string, error?: Error | unknown, context?: string): void {
    const errorData: LogMetadata = {
      message,
      context,
      timestamp: new Date().toISOString(),
    };

    if (error) {
      this.sanitizeErrorObject(error);
    }

    const sanitizedData = this.sanitizePayload(errorData) as LogMetadata;

    if (this.isDevelopment) {
      console.error(`❌ ${message}`, sanitizedData, error);
    }

    // Capture to Sentry based on error type
    if (error instanceof Error) {
      Sentry.captureException(error, {
        contexts: {
          custom: sanitizedData,
        },
        tags: {
          context: context || 'unknown',
        },
      });
    } else if (error) {
      // Non-Error objects
      Sentry.captureMessage(message, {
        level: 'error',
        contexts: {
          custom: { ...sanitizedData, error: this.sanitizePayload(error) },
        },
        tags: {
          context: context || 'unknown',
        },
      });
    } else {
      // No error object, just message
      Sentry.captureMessage(message, {
        level: 'error',
        contexts: {
          custom: sanitizedData,
        },
      });
    }
  }

  /**
   * Debug messages (development only)
   * - Dev: Console debug
   * - Prod: Silent (no overhead)
   *
   * @param message - Debug message
   * @param data - Optional debug data
   */
  public debug(message: string, data?: unknown): void {
    if (this.isDevelopment) {
      console.debug(`🔍 ${message}`, data || '');
    }
  }

  /**
   * Success messages (development only, for UX feedback)
   * - Dev: Console log with green indicator
   * - Prod: Breadcrumb only
   *
   * @param message - Success message
   * @param meta - Optional metadata
   */
  public success(message: string, meta?: LogMetadata): void {
    if (this.isDevelopment) {
      console.log(`✅ ${message}`, meta || '');
    }

    Sentry.addBreadcrumb({
      message,
      level: 'info',
      data: meta,
    });
  }

  /**
   * Cleans sensitive request/response payload context from Error objects.
   * Modifies the custom error properties in-place before console output or Sentry transmission.
   */
  private sanitizeErrorObject(error: unknown): void {
    if (!error || typeof error !== 'object') {
      return;
    }

    const errObj = error as Record<string, unknown>;

    // Sanitize any attached network request configurations
    if (errObj.config) {
      errObj.config = this.sanitizePayload(errObj.config);
    }

    // Sanitize any attached network response representations
    if (errObj.response) {
      errObj.response = this.sanitizePayload(errObj.response);
    }
  }

  /**
   * Sanitizes log payloads to remove sensitive PII.
   * keys to mask: password, token, secret, creditCard, phoneNumber (if needed)
   */
  private sanitizePayload(data: unknown): unknown {
    if (typeof data === 'string') {
      const trimmed = data.trim();
      // Try to parse stringified JSON if it looks like JSON to sanitize nested values
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(data);
          const sanitized = this.sanitizePayload(parsed);
          return JSON.stringify(sanitized);
        } catch {
          // Fallback if not valid JSON
        }
      }

      // Mask credentials/secrets in query strings or JSON-like formats
      let sanitizedString = data;
      const sensitivePatterns = [
        /(password|token|secret|key|authorization|cookie|creditCard|cvv)=([^&]+)/gi,
        /("password"|"token"|"secret"|"key"|"authorization"|"cookie"|"creditCard"|"cvv")\s*:\s*"([^"]+)"/gi
      ];
      for (const pattern of sensitivePatterns) {
        sanitizedString = sanitizedString.replace(pattern, (match, p2) => {
          return match.replace(p2, '[REDACTED]');
        });
      }
      return sanitizedString;
    }

    if (!data || typeof data !== 'object') {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitizePayload(item));
    }

    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'authorization', 'cookie', 'creditCard', 'cvv'];
    const sanitized: Record<string, unknown> = { ...data as Record<string, unknown> };

    for (const key of Object.keys(sanitized)) {
      if (sensitiveKeys.some(s => key.toLowerCase().includes(s))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitizePayload(sanitized[key]);
      }
    }

    return sanitized;
  }
}

export default new LoggerService();
